import type { Comic } from '@/mocks/comics';

interface ReaderProps {
  comic: Comic;
  chapter: number;
  total: number;
  onPrev: () => void;
  onNext: () => void;
  onClose: () => void;
}

export default function Reader({ comic, chapter, total, onPrev, onNext, onClose }: ReaderProps) {
  const panels = comic.panels && comic.panels.length > 0 ? comic.panels : [comic.cover];

  return (
    <section id="reader" className="border-t border-background-200 bg-background-100" aria-label={`《${comic.title}》第${chapter}话阅读器`}>
      <div className="sticky top-16 z-40 border-b border-background-200 bg-background-50/95 backdrop-blur-md md:top-20">
        <div className="mx-auto flex max-w-4xl flex-wrap items-center gap-2 px-4 py-2.5 md:px-6 md:py-3">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <span className="hidden h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary-500 sm:flex">
              <i className="ri-book-open-line h-4 w-4 text-background-50" aria-hidden="true"></i>
            </span>
            <h2 className="truncate text-sm font-bold text-foreground-900 md:text-base">
              《{comic.title}》 第{chapter}话
            </h2>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={onPrev}
              disabled={chapter <= 1}
              className="flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-2.5 py-1.5 text-xs font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <i className="ri-arrow-left-s-line h-4 w-4" aria-hidden="true"></i>
              上一话
            </button>
            <button
              type="button"
              onClick={onNext}
              disabled={chapter >= total}
              className="flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-2.5 py-1.5 text-xs font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-40"
            >
              下一话
              <i className="ri-arrow-right-s-line h-4 w-4" aria-hidden="true"></i>
            </button>
            <button
              type="button"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="flex h-8 w-8 items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600"
              aria-label="回到页面顶部"
            >
              <i className="ri-arrow-up-line h-4 w-4" aria-hidden="true"></i>
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-3 py-1.5 text-xs font-bold text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-close-line h-4 w-4" aria-hidden="true"></i>
              返回详情
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-3xl px-4 py-6 md:px-6 md:py-8">
        <p className="mb-4 flex items-center justify-center gap-2 text-xs text-foreground-500 md:text-sm">
          <i className="ri-arrow-down-double-line h-4 w-4 text-primary-500" aria-hidden="true"></i>
          下拉滚动阅读，沉浸式体验
        </p>
        <div className="flex flex-col gap-3 md:gap-4">
          {panels.map((src, index) => (
            <div key={`${comic.id}-${chapter}-${index}`} className="reader-page relative overflow-hidden rounded-lg bg-background-200">
              <img
                src={src}
                alt={`《${comic.title}》第${chapter}话分镜${index + 1}`}
                title={`《${comic.title}》第${chapter}话 ${index + 1}/${panels.length}`}
                className="w-full object-cover object-top"
              />
            </div>
          ))}
        </div>

        <div className="mt-6 flex items-center justify-between gap-3 border-t border-background-200 pt-5">
          <button
            type="button"
            onClick={onPrev}
            disabled={chapter <= 1}
            className="flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-bold text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <i className="ri-arrow-left-line h-4 w-4" aria-hidden="true"></i>
            上一话
          </button>
          <span className="text-xs text-foreground-500 md:text-sm">
            第 {chapter} / {total} 话
          </span>
          <button
            type="button"
            onClick={onNext}
            disabled={chapter >= total}
            className="flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-bold text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-40"
          >
            下一话
            <i className="ri-arrow-right-line h-4 w-4" aria-hidden="true"></i>
          </button>
        </div>
        <p className="mt-4 text-center text-xs text-foreground-400 md:text-sm">
          《{comic.title}》第{chapter}话 · 57漫画网 下拉式免费阅读
        </p>
      </div>
    </section>
  );
}